import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Card,
  CardContent,
  Paper,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Badge,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Download, Send } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import { getPortalToken, clearPortalToken } from '../../lib/authToken';
import PortalCallWatcher from '../../components/Portal/PortalCallWatcher';
import PortalCallbackButton from '../../components/Portal/PortalCallbackButton';

const portalHeaders = () => ({
  Authorization: `Bearer ${getPortalToken()}`,
  'Content-Type': 'application/json',
});

const POLL_MS = 12000;

const messageTimestamp = (m: { createdAt?: string; created_at?: string }) =>
  m.createdAt || m.created_at || null;

const formatMessageWhen = (m: { createdAt?: string; created_at?: string }) => {
  const raw = messageTimestamp(m);
  if (!raw) return '';
  try {
    return format(new Date(raw), 'MMM d, yyyy h:mm a');
  } catch {
    return '';
  }
};

const normalizePortalMessage = (m: any) => ({
  ...m,
  createdAt: m.createdAt || m.created_at || null,
  updatedAt: m.updatedAt || m.updated_at || null,
});

const PortalDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(0);
  const [cases, setCases] = useState<any[]>([]);
  const [documents, setDocuments] = useState<any[]>([]);
  const [messages, setMessages] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);
  const [clientPayments, setClientPayments] = useState<any[]>([]);
  const [payingId, setPayingId] = useState<string | null>(null);
  const [payNotice, setPayNotice] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeParentId, setComposeParentId] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [consents, setConsents] = useState<any[]>([]);
  const [signRequests, setSignRequests] = useState<any[]>([]);
  const [consentType, setConsentType] = useState('privacy_notice');
  const [consentCaseId, setConsentCaseId] = useState('');
  const [signatureName, setSignatureName] = useState('');
  const [signing, setSigning] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  const client = (() => {
    try {
      return JSON.parse(localStorage.getItem('portalClient') || 'null');
    } catch {
      return null;
    }
  })();
  const user = (() => {
    try {
      return JSON.parse(localStorage.getItem('portalUser') || 'null');
    } catch {
      return null;
    }
  })();

  useEffect(() => {
    const u = (() => {
      try {
        return JSON.parse(localStorage.getItem('portalUser') || 'null');
      } catch {
        return null;
      }
    })();
    if (u?.requiresPasswordChange || u?.isFirstLogin) {
      setMustChangePassword(true);
      return;
    }
    // Confirm with server (covers sessions created before password-change flags existed)
    const token = getPortalToken();
    if (!token) return;
    fetch('/api/portal/me', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((json) => {
        if (json?.data?.user?.requiresPasswordChange || json?.data?.user?.isFirstLogin) {
          localStorage.setItem(
            'portalUser',
            JSON.stringify({
              ...(u || {}),
              ...json.data.user,
            })
          );
          setMustChangePassword(true);
        }
      })
      .catch(() => {
        /* non-blocking */
      });
  }, []);

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = Boolean(opts?.silent);
    if (!silent) setLoading(true);
    try {
      const [casesRes, docsRes, msgRes, consentRes, invRes, signRes, payRes] = await Promise.all([
        fetch('/api/portal/cases', { headers: portalHeaders() }),
        fetch('/api/portal/documents', { headers: portalHeaders() }),
        fetch('/api/portal/messages', { headers: portalHeaders() }),
        fetch('/api/portal/consents', { headers: portalHeaders() }),
        fetch('/api/portal/invoices', { headers: portalHeaders() }),
        fetch('/api/portal/signature-requests', { headers: portalHeaders() }),
        fetch('/api/portal/payments', { headers: portalHeaders() }),
      ]);
      const casesJson = await casesRes.json();
      const docsJson = await docsRes.json();
      const msgJson = await msgRes.json();
      const consentJson = await consentRes.json();
      const invJson = await invRes.json().catch(() => ({ data: [] }));
      const signJson = await signRes.json().catch(() => ({ data: [] }));
      const payJson = await payRes.json().catch(() => ({ data: [] }));
      if (!casesRes.ok) throw new Error(casesJson.message || 'Failed to load cases');
      if (!docsRes.ok) throw new Error(docsJson.message || 'Failed to load documents');
      if (!msgRes.ok) throw new Error(msgJson.message || 'Failed to load messages');
      setCases(casesJson.data || []);
      setDocuments(docsJson.data || []);
      setMessages((msgJson.data || []).map(normalizePortalMessage));
      setConsents(consentRes.ok ? (consentJson.data || []) : []);
      setInvoices(invRes.ok ? (invJson.data || []) : []);
      setPaymentsEnabled(Boolean(invJson.paymentsEnabled));
      setSignRequests(signRes.ok ? (signJson.data || []) : []);
      setClientPayments(payRes.ok ? (payJson.data || []) : []);
      setError('');
    } catch (err: any) {
      if (!silent) {
        setError(err.message || 'Failed to load portal data');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const refreshMessages = useCallback(async () => {
    try {
      const res = await fetch('/api/portal/messages', { headers: portalHeaders() });
      const json = await res.json();
      if (!res.ok) return;
      setMessages((json.data || []).map(normalizePortalMessage));
    } catch {
      /* non-blocking poll */
    }
  }, []);

  useEffect(() => {
    if (!getPortalToken()) {
      navigate('/portal/login');
      return;
    }
    if (mustChangePassword) {
      setLoading(false);
      return;
    }
    load();
  }, [navigate, load, mustChangePassword]);

  useEffect(() => {
    if (searchParams.get('tab') === 'billing') setTab(2);
    if (searchParams.get('cancelled') === '1') {
      setPayNotice('Payment was cancelled. No charge was applied.');
    }
  }, [searchParams]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId || !getPortalToken()) return;
    fetch('/api/portal/payments/complete', {
      method: 'POST',
      headers: portalHeaders(),
      body: JSON.stringify({ sessionId }),
    })
      .then(async (res) => {
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.message || 'Could not confirm payment');
        if (json.data?.pending) {
          setPayNotice('Bank payment received. It will post to the invoice when it settles.');
        } else {
          setPayNotice('Payment received and applied to your invoice.');
        }
        toast.success('Payment applied');
        await load({ silent: true });
      })
      .catch((err) => {
        setPayNotice(err instanceof Error ? err.message : 'Could not confirm payment');
      });
  }, [searchParams, load]);

  // Poll for new messages / portal updates while the tab is open
  useEffect(() => {
    if (mustChangePassword || !getPortalToken()) return;

    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      if (tab === 1) {
        void refreshMessages();
      } else {
        void load({ silent: true });
      }
    };

    const id = window.setInterval(tick, POLL_MS);
    const onFocus = () => {
      void load({ silent: true });
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void load({ silent: true });
      }
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.clearInterval(id);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [mustChangePassword, tab, load, refreshMessages]);

  // Immediate refresh when opening Messages
  useEffect(() => {
    if (mustChangePassword || tab !== 1) return;
    void refreshMessages();
  }, [tab, mustChangePassword, refreshMessages]);

  const handleSavePortalPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setSavingPassword(true);
    try {
      const res = await fetch('/api/portal/first-time-password-change', {
        method: 'POST',
        headers: portalHeaders(),
        body: JSON.stringify({ newPassword }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Unable to set password');
      const stored = JSON.parse(localStorage.getItem('portalUser') || '{}');
      localStorage.setItem(
        'portalUser',
        JSON.stringify({
          ...stored,
          isFirstLogin: false,
          requiresPasswordChange: false,
        })
      );
      setMustChangePassword(false);
      toast.success('Password saved');
    } catch (err: any) {
      toast.error(err.message || 'Unable to set password');
    } finally {
      setSavingPassword(false);
    }
  };

  const payInvoice = async (inv: any) => {
    setPayingId(inv.id);
    try {
      const res = await fetch(`/api/portal/invoices/${inv.id}/checkout`, {
        method: 'POST',
        headers: portalHeaders(),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Unable to start payment');
      if (json.data?.url) {
        window.location.href = json.data.url;
        return;
      }
      throw new Error('Payment page was not returned');
    } catch (err: any) {
      toast.error(err.message || 'Unable to start payment');
      setPayingId(null);
    }
  };

  const unreadMessages = useMemo(
    () =>
      messages.filter(
        (m) => m.senderType !== 'client' && !m.isRead
      ),
    [messages]
  );

  const selectedMessage = useMemo(
    () => messages.find((m) => m.id === selectedMessageId) || null,
    [messages, selectedMessageId]
  );

  const markMessageRead = useCallback(async (messageId: string) => {
    try {
      const res = await fetch(`/api/portal/messages/${messageId}/read`, {
        method: 'POST',
        headers: portalHeaders(),
      });
      if (!res.ok) return;
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId ? { ...m, isRead: true, readAt: new Date().toISOString() } : m
        )
      );
    } catch {
      /* non-blocking */
    }
  }, []);

  const selectMessage = useCallback(
    (messageId: string) => {
      setSelectedMessageId(messageId);
      const msg = messages.find((m) => m.id === messageId);
      if (msg && msg.senderType !== 'client' && !msg.isRead) {
        void markMessageRead(messageId);
      }
    },
    [messages, markMessageRead]
  );
  const logout = () => {
    clearPortalToken();
    localStorage.removeItem('portalClient');
    localStorage.removeItem('portalUser');
    navigate('/portal/login');
  };

  const downloadDocument = async (doc: any) => {
    try {
      const res = await fetch(`/api/portal/documents/${doc.id}/download`, {
        headers: { Authorization: `Bearer ${getPortalToken()}` },
      });
      if (!res.ok) {
        const contentType = res.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          const errJson = await res.json();
          if (errJson.code === 'DOCUMENT_NOT_RELEASED') {
            toast.error('Document not yet released by your attorney');
            return;
          }
          throw new Error(errJson.message || 'Download failed');
        }
        throw new Error('Download failed');
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.originalName || doc.fileName || 'document';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      toast.error(err.message || 'Failed to download');
    }
  };

  const downloadInvoicePdf = async (inv: any) => {
    try {
      const res = await fetch(`/api/portal/invoices/${inv.id}/download`, {
        headers: { Authorization: `Bearer ${getPortalToken()}` },
      });
      if (!res.ok) {
        const contentType = res.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          const errJson = await res.json();
          throw new Error(errJson.message || 'Download failed');
        }
        throw new Error('Download failed');
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${inv.invoiceNumber || inv.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Invoice downloaded');
    } catch (err: any) {
      toast.error(err.message || 'Failed to download invoice');
    }
  };

  const filteredDocuments = useMemo(() => {
    if (!selectedCaseId) return documents;
    return documents.filter(
      (d) => d.caseId === selectedCaseId || d.case?.id === selectedCaseId
    );
  }, [documents, selectedCaseId]);

  const sendMessage = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      const res = await fetch('/api/portal/messages', {
        method: 'POST',
        headers: portalHeaders(),
        body: JSON.stringify({
          subject: subject || undefined,
          body,
          parentId: composeParentId || undefined,
          caseId: selectedCaseId || undefined,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to send');
      toast.success('Message sent');
      setComposeOpen(false);
      setComposeParentId(null);
      setSubject('');
      setBody('');
      await load();
    } catch (err: any) {
      toast.error(err.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const submitConsent = async () => {
    if (!signatureName.trim()) {
      toast.error('Type your full legal name to sign');
      return;
    }
    setSigning(true);
    try {
      const res = await fetch('/api/portal/consents', {
        method: 'POST',
        headers: portalHeaders(),
        body: JSON.stringify({
          consentType,
          signatureName: signatureName.trim(),
          accepted: true,
          caseId: consentCaseId || undefined,
          documentVersion: '1.0',
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to record acknowledgment');
      toast.success('Acknowledgment recorded');
      setSignatureName('');
      await load();
    } catch (err: any) {
      toast.error(err.message || 'Failed to sign');
    } finally {
      setSigning(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static" color="primary" elevation={0}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Client Portal
          </Typography>
          {user && !mustChangePassword && <PortalCallbackButton cases={cases} />}
          <Typography variant="body2" sx={{ mr: 2 }}>
            {user ? `${user.firstName} ${user.lastName}` : ''}
          </Typography>
          <Button color="inherit" onClick={logout}>
            Sign out
          </Button>
        </Toolbar>
      </AppBar>
      {/* Wave CM1 — rings when the firm calls while the portal is open. */}
      {user && !mustChangePassword && <PortalCallWatcher />}

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {mustChangePassword ? (
          <Paper sx={{ p: 3, maxWidth: 480 }}>
            <Typography variant="h5" gutterBottom fontWeight={600}>
              Set your password
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              You signed in with a temporary password. Choose a permanent password only you know
              before using the portal.
            </Typography>
            <Box component="form" onSubmit={handleSavePortalPassword}>
              <TextField
                fullWidth
                type="password"
                label="New password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                type="password"
                label="Confirm password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                margin="normal"
                required
                error={Boolean(confirmPassword) && confirmPassword !== newPassword}
                helperText={
                  confirmPassword && confirmPassword !== newPassword
                    ? 'Passwords do not match'
                    : ' '
                }
              />
              <Button
                type="submit"
                variant="contained"
                disabled={savingPassword || !newPassword || newPassword !== confirmPassword}
                sx={{ mt: 1 }}
              >
                {savingPassword ? <CircularProgress size={22} color="inherit" /> : 'Save password'}
              </Button>
            </Box>
          </Paper>
        ) : (
          <>
        <Typography variant="h4" gutterBottom>
          Welcome{client ? `, ${client.firstName}` : ''}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          View matters, download shared documents, and message your legal team.
        </Typography>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
          <Tab label="Cases & Documents" />
          <Tab
            label={
              <Badge
                color="error"
                badgeContent={unreadMessages.length}
                invisible={unreadMessages.length === 0}
              >
                Messages
              </Badge>
            }
          />
          <Tab label="Billing" />
          <Tab label="Acknowledgments" />
        </Tabs>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : tab === 0 ? (
          <Grid container spacing={3}>
            {signRequests.filter((r) => r.status === 'sent' || r.status === 'viewed').length > 0 && (
              <Grid item xs={12}>
                <Alert severity="warning" sx={{ mb: 0 }}>
                  You have documents waiting for your electronic signature. Open the signing page to view the exact PDF — this is not the Acknowledgments tab.
                </Alert>
                <List dense>
                  {signRequests
                    .filter((r) => r.status === 'sent' || r.status === 'viewed')
                    .map((r) => (
                      <ListItem key={r.id} disablePadding>
                        <ListItemButton onClick={() => navigate(`/portal/sign/${r.id}`)}>
                          <ListItemText
                            primary={r.title || 'Document to sign'}
                            secondary={`${String(r.instrumentType || '').replace(/_/g, ' ')} · ${r.status}`}
                          />
                          <Button size="small">Sign</Button>
                        </ListItemButton>
                      </ListItem>
                    ))}
                </List>
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Your cases
                  </Typography>
                  {cases.length === 0 ? (
                    <Typography color="text.secondary">
                      No matters are linked to your portal account yet. Contact your attorney if you expected to see a case here.
                    </Typography>
                  ) : (
                    <>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        <Chip
                          label="All cases"
                          size="small"
                          color={selectedCaseId === null ? 'primary' : 'default'}
                          variant={selectedCaseId === null ? 'filled' : 'outlined'}
                          onClick={() => setSelectedCaseId(null)}
                        />
                        {cases.map((c) => (
                          <Chip
                            key={c.id}
                            label={c.caseNumber || c.title}
                            size="small"
                            color={selectedCaseId === c.id ? 'primary' : 'default'}
                            variant={selectedCaseId === c.id ? 'filled' : 'outlined'}
                            onClick={() => setSelectedCaseId(c.id)}
                          />
                        ))}
                      </Box>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Number</TableCell>
                            <TableCell>Title</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {cases.map((c) => (
                            <TableRow
                              key={c.id}
                              hover
                              selected={selectedCaseId === c.id}
                              sx={{ cursor: 'pointer' }}
                              onClick={() => setSelectedCaseId(c.id)}
                            >
                              <TableCell>{c.caseNumber}</TableCell>
                              <TableCell>{c.title}</TableCell>
                              <TableCell>
                                <Chip size="small" label={c.status} />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Shared documents
                    {selectedCaseId
                      ? ` — ${cases.find((c) => c.id === selectedCaseId)?.caseNumber || 'case'}`
                      : ''}
                  </Typography>
                  {filteredDocuments.length === 0 ? (
                    <Typography color="text.secondary">
                      {documents.length === 0
                        ? 'Your attorney has not shared any documents yet.'
                        : 'No documents for the selected case. Choose another case chip or All cases.'}
                    </Typography>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Title</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell align="right">Download</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredDocuments.map((d) => (
                          <TableRow key={d.id}>
                            <TableCell>{d.title}</TableCell>
                            <TableCell>{d.documentType}</TableCell>
                            <TableCell>
                              {d.createdAt
                                ? format(new Date(d.createdAt), 'MMM d, yyyy')
                                : '—'}
                            </TableCell>
                            <TableCell align="right">
                              <IconButton size="small" onClick={() => downloadDocument(d)}>
                                <Download fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        ) : tab === 1 ? (
          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, gap: 1 }}>
                    <Typography variant="h6">Messages</Typography>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Send />}
                      onClick={() => {
                        setComposeParentId(null);
                        setSubject('');
                        setBody('');
                        setComposeOpen(true);
                      }}
                    >
                      New message
                    </Button>
                  </Box>
                  {messages.length === 0 ? (
                    <Typography color="text.secondary">
                      No messages yet. Use New message to reach your legal team.
                    </Typography>
                  ) : (
                    <List disablePadding sx={{ maxHeight: 520, overflow: 'auto' }}>
                      {messages.map((m, idx) => {
                        const unread = m.senderType !== 'client' && !m.isRead;
                        const selected = selectedMessageId === m.id;
                        return (
                          <React.Fragment key={m.id}>
                            <ListItem disablePadding alignItems="flex-start">
                              <ListItemButton
                                selected={selected}
                                onClick={() => selectMessage(m.id)}
                                sx={{
                                  alignItems: 'flex-start',
                                  bgcolor: unread && !selected ? 'action.hover' : undefined,
                                }}
                              >
                                <ListItemText
                                  primary={
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        gap: 1,
                                        alignItems: 'center',
                                        flexWrap: 'wrap',
                                      }}
                                    >
                                      <Typography
                                        variant="subtitle2"
                                        sx={{ fontWeight: unread ? 700 : 600 }}
                                      >
                                        {m.subject || '(No subject)'}
                                      </Typography>
                                      <Chip
                                        size="small"
                                        label={m.senderType === 'client' ? 'You' : 'Firm'}
                                        color={m.senderType === 'client' ? 'default' : 'primary'}
                                      />
                                      {unread && (
                                        <Chip
                                          size="small"
                                          label="Unread"
                                          color="error"
                                          variant="outlined"
                                        />
                                      )}
                                    </Box>
                                  }
                                  secondary={
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        mt: 0.5,
                                      }}
                                    >
                                      {formatMessageWhen(m)
                                        ? `${formatMessageWhen(m)} · ${m.body || ''}`
                                        : m.body || ''}
                                    </Typography>
                                  }
                                />
                              </ListItemButton>
                            </ListItem>
                            {idx < messages.length - 1 && <Divider component="li" />}
                          </React.Fragment>
                        );
                      })}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={7}>
              <Card sx={{ height: '100%', minHeight: 280 }}>
                <CardContent>
                  {!selectedMessage ? (
                    <Box sx={{ py: 6, textAlign: 'center' }}>
                      <Typography color="text.secondary">
                        Select a message to read the full conversation item.
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 1,
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          mb: 1,
                        }}
                      >
                        <Typography variant="h6" sx={{ flexGrow: 1 }}>
                          {selectedMessage.subject || '(No subject)'}
                        </Typography>
                        <Chip
                          size="small"
                          label={selectedMessage.senderType === 'client' ? 'You' : 'Firm'}
                          color={selectedMessage.senderType === 'client' ? 'default' : 'primary'}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                        {formatMessageWhen(selectedMessage) || 'Date unavailable'}
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {selectedMessage.body}
                      </Typography>
                      <Box sx={{ mt: 3 }}>
                        <Button
                          variant="outlined"
                          startIcon={<Send />}
                          onClick={() => {
                            setComposeParentId(selectedMessage.id);
                            setSubject(
                              selectedMessage.subject
                                ? `Re: ${String(selectedMessage.subject).replace(/^Re:\s*/i, '')}`
                                : ''
                            );
                            setComposeOpen(true);
                          }}
                        >
                          Reply
                        </Button>
                      </Box>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        ) : tab === 2 ? (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Billing
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Invoices from your firm. Online payments apply to the invoice balance (operating account — not a trust deposit).
              </Typography>
              {payNotice && (
                <Alert severity="info" sx={{ mb: 2 }} onClose={() => setPayNotice('')}>
                  {payNotice}
                </Alert>
              )}
              {invoices.length === 0 ? (
                <Typography color="text.secondary">No invoices available.</Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Invoice</TableCell>
                      <TableCell>Issued</TableCell>
                      <TableCell>Due</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right">Balance</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Pay</TableCell>
                      <TableCell align="right">PDF</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoices.map((inv) => (
                      <TableRow key={inv.id}>
                        <TableCell>{inv.invoiceNumber}</TableCell>
                        <TableCell>
                          {inv.issueDate
                            ? format(new Date(inv.issueDate), 'MMM d, yyyy')
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {inv.dueDate
                            ? format(new Date(inv.dueDate), 'MMM d, yyyy')
                            : '—'}
                        </TableCell>
                        <TableCell align="right">
                          ${Number(inv.totalAmount || 0).toFixed(2)}
                        </TableCell>
                        <TableCell align="right">
                          ${Number(inv.balanceDue || 0).toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={String(inv.status || '').replace(/_/g, ' ')}
                            color={
                              inv.status === 'paid'
                                ? 'success'
                                : inv.status === 'overdue'
                                  ? 'error'
                                  : 'default'
                            }
                          />
                        </TableCell>
                        <TableCell align="right">
                          {paymentsEnabled
                          && Number(inv.balanceDue || 0) > 0
                          && ['sent', 'overdue', 'partially_paid'].includes(String(inv.status)) ? (
                            <Button
                              size="small"
                              variant="contained"
                              disabled={payingId === inv.id}
                              onClick={() => payInvoice(inv)}
                            >
                              {payingId === inv.id ? 'Opening…' : 'Pay'}
                            </Button>
                          ) : Number(inv.balanceDue || 0) <= 0 ? (
                            <Typography variant="caption" color="success.main">Paid</Typography>
                          ) : (
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            aria-label={`Download invoice ${inv.invoiceNumber}`}
                            onClick={() => downloadInvoicePdf(inv)}
                          >
                            <Download fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {clientPayments.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Payment applications
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Invoice</TableCell>
                        <TableCell>Method</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {clientPayments.map((p) => (
                        <TableRow key={p.id}>
                          <TableCell>
                            {p.appliedAt || p.createdAt
                              ? format(new Date(p.appliedAt || p.createdAt), 'MMM d, yyyy')
                              : '—'}
                          </TableCell>
                          <TableCell>{p.invoice?.invoiceNumber || '—'}</TableCell>
                          <TableCell>{String(p.method || '').replace(/_/g, ' ')}</TableCell>
                          <TableCell align="right">${Number(p.amount || 0).toFixed(2)}</TableCell>
                          <TableCell>{p.status}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Acknowledgments on file
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Type your full legal name to acknowledge privacy, communication consent, or intake accuracy.
                    This is <strong>not</strong> an electronic signature of a retainer or other legal instrument.
                    Documents to sign appear as “To sign” on Cases &amp; Documents and at /portal/sign/….
                  </Typography>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="portal-consent-type-label">Acknowledgment type</InputLabel>
                    <Select
                      labelId="portal-consent-type-label"
                      label="Acknowledgment type"
                      value={consentType}
                      onChange={(e) => setConsentType(String(e.target.value))}
                    >
                      <MenuItem value="privacy_notice">Privacy notice</MenuItem>
                      <MenuItem value="electronic_communication">Electronic communication</MenuItem>
                      <MenuItem value="intake_accuracy">Intake information accuracy</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="portal-consent-case-label">Related case (optional)</InputLabel>
                    <Select
                      labelId="portal-consent-case-label"
                      label="Related case (optional)"
                      value={consentCaseId || '__none__'}
                      onChange={(e) => {
                        const v = String(e.target.value);
                        setConsentCaseId(v === '__none__' ? '' : v);
                      }}
                    >
                      <MenuItem value="__none__">
                        <em>Firm-wide / no specific case</em>
                      </MenuItem>
                      {cases.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.caseNumber} — {c.title}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    fullWidth
                    label="Type full legal name"
                    value={signatureName}
                    onChange={(e) => setSignatureName(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  <Button
                    variant="contained"
                    disabled={signing || !signatureName.trim()}
                    onClick={submitConsent}
                  >
                    {signing ? <CircularProgress size={22} /> : 'Sign & submit'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Your acknowledgments
                  </Typography>
                  {consents.length === 0 ? (
                    <Typography color="text.secondary">No acknowledgments on file yet.</Typography>
                  ) : (
                    <List dense>
                      {consents.map((c) => (
                        <ListItem key={c.id} divider>
                          <ListItemText
                            primary={`${c.consentType.replace(/_/g, ' ')} · ${c.signatureName || 'Signed'}`}
                            secondary={
                              c.acceptedAt
                                ? format(new Date(c.acceptedAt), 'MMM d, yyyy h:mm a')
                                : c.createdAt
                                  ? format(new Date(c.createdAt), 'MMM d, yyyy h:mm a')
                                  : ''
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
          </>
        )}
      </Container>

      <Dialog
        open={composeOpen}
        onClose={() => {
          setComposeOpen(false);
          setComposeParentId(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{composeParentId ? 'Reply to message' : 'Message your legal team'}</DialogTitle>
        <DialogContent>
          {composeParentId ? (
            <Alert severity="info" sx={{ mt: 1, mb: 1 }}>
              This reply will stay in the same conversation thread.
            </Alert>
          ) : null}
          <TextField
            fullWidth
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Message"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setComposeOpen(false);
              setComposeParentId(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!body.trim() || sending}
            onClick={sendMessage}
          >
            {sending ? <CircularProgress size={22} /> : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PortalDashboardPage;
